import { useCallback } from 'react';
import { RecommendationsTriggeredBy } from '@kouto/types';
import generateListingRecommendations from 'api/listingRecommendation/generateListingRecommendations';
import { formatDateRange } from 'utils/date/formatDateRange';
import useSupportedLanguage from 'utils/data/language/useSupportedLanguage';

export interface GenerateRecommendationsParams {
  experienceIds: string[];
  resourceGroupCollectionIds: string[];
  sessionDate: string | Date;
  brandId: string;
  dateRangeDays?: number;
  triggeredBy?: RecommendationsTriggeredBy;
}

const useGenerateListingRecommendations = () => {
  const languageCode = useSupportedLanguage();

  const generateRecommendations = useCallback(
    async ({
      experienceIds,
      resourceGroupCollectionIds,
      sessionDate,
      brandId,
      dateRangeDays = 2,
      triggeredBy = RecommendationsTriggeredBy.UPSELL,
    }: GenerateRecommendationsParams): Promise<void> => {
      const sessionDateObj =
        typeof sessionDate === 'string' ? new Date(sessionDate) : sessionDate;

      const dateOnly = sessionDateObj.toISOString().split('T')[0];
      const normalizedSessionDate = new Date(`${dateOnly}T00:00:00Z`);

      const fromDateUTC = new Date(normalizedSessionDate);
      fromDateUTC.setUTCDate(fromDateUTC.getUTCDate() - dateRangeDays);

      const toDateUTC = new Date(normalizedSessionDate);
      toDateUTC.setUTCDate(toDateUTC.getUTCDate() + dateRangeDays);

      const fromStr = fromDateUTC.toISOString().split('T')[0];
      const toStr = toDateUTC.toISOString().split('T')[0];

      const formattedDateRange = formatDateRange(fromStr, toStr);

      const recommendationResponse = await generateListingRecommendations(
        brandId,
        {
          brandId,
          experienceIds,
          from: fromStr,
          language: languageCode,
          resourceGroupCollectionIds,
          to: toStr,
          triggeredBy,
        },
      );

      window.sessionStorage.setItem(
        'recommendation_id',
        recommendationResponse.recommendationId,
      );
      window.sessionStorage.setItem(
        'recommendation_date_range',
        formattedDateRange,
      );
    },
    [],
  );

  return { generateRecommendations };
};

export default useGenerateListingRecommendations;
